import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { catchError, EMPTY, Observable, ReplaySubject, switchMap, take, tap } from 'rxjs';
import { CallApiService } from '../services/api-action.service';
import { UserSettingsService } from '../services/user-settings.service';
import { ToastrService } from 'ngx-toastr';
import { MAX_PAGE_SIZE, PagedListState } from './PagedListState';
import { PagedResult, PagingParams } from './PagedResult';

export interface PagedListOptions<T> {
  /** Grid field → server sort key; see {@link PagedListState}. */
  sortFields: Record<string, string>;
  defaultSort?: SortDescriptor[];
  /**
   * The request for the current page. Return `null` while there is nothing to load (no profile
   * yet, no filter, a role that sees no list) — the spinner then gives way to "no records".
   */
  fetch: (paging: PagingParams) => Observable<PagedResult<T>> | null;
  /** Toasted when a load fails; a generic sentence when omitted. */
  errorMessage?: string;
  /** Called with every page that reaches the grid. */
  onLoaded?: (page: PagedResult<T>) => void;
}

/**
 * #199: the fetching half of a server-paged kendo-grid, which the list components used to copy
 * one by one. {@link PagedListState} keeps skip/take/sort; this owns the requests:
 *
 * - **Latest request wins.** Every load cancels the one still in flight (`switchMap`, and
 *   HttpClient aborts the call), so a slow response can no longer overwrite a newer page or
 *   the result of a newer filter. That is also why `loading` needs no counter of open
 *   requests: it is true exactly while one is running.
 * - **Page size first.** Nothing is requested before the user's list page size is known.
 * - **A failed load is not the end.** The error is toasted and the next load works — an
 *   error escaping the `switchMap` would silently end the list for the rest of the visit.
 * - **No empty page past the end.** After a removal the page steps back (#69).
 *
 * Create it in a field initialiser: it takes `DestroyRef`, `UserSettingsService` and
 * `CallApiService` via `inject()`, and unsubscribes when the component is destroyed. The
 * template binds `list.gridData`, `list.state.skip|pageSize|sort`, `list.loading` and forwards
 * `(pageChange)` / `(sortChange)` to `list.onPageChange` / `list.onSortChange`.
 */
export class PagedList<T> {
  public readonly state: PagedListState;
  public gridData: GridDataResult = { data: [], total: 0 };
  /** Starts true: every list loads on init, so "no records" must not flash first. */
  public loading = true;

  // ReplaySubject(1): a load() asked for before the page size is known is kept, not lost.
  private readonly requests = new ReplaySubject<void>(1);
  private readonly toastr = inject(ToastrService);

  constructor(options: PagedListOptions<T>) {
    this.state = new PagedListState(options.sortFields, options.defaultSort);
    const callApi = inject(CallApiService);
    const errorMessage = options.errorMessage ?? $localize`Die Liste konnte nicht geladen werden.`;

    inject(UserSettingsService)
      .listPageSize$.pipe(
        take(1),
        tap((size) => (this.state.pageSize = size)),
        switchMap(() => this.requests),
        switchMap(() => {
          const page$ = options.fetch(this.state.toParams());
          if (!page$) {
            this.loading = false;
            return EMPTY;
          }
          this.loading = true;
          return callApi.callWithToasts$(page$, null, errorMessage).pipe(
            catchError(() => {
              this.loading = false;
              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(inject(DestroyRef)),
      )
      .subscribe((page) => {
        if (this.state.clampToTotal(page.totalCount)) {
          this.load();
          return;
        }
        this.gridData = { data: page.items, total: page.totalCount };
        this.loading = false;
        options.onLoaded?.(page);
      });
  }

  /** Loads the current page again — after a change to the rows, or once `fetch` can answer. */
  public load(): void {
    this.requests.next();
  }

  /** A new filter: the total changed, so start over at the first page. */
  public search(): void {
    this.state.resetPage();
    this.load();
  }

  public onPageChange(event: PageChangeEvent): void {
    // Only a PDF export asks for more than a page; above the cap it gets the first 500 rows.
    if (event.take > MAX_PAGE_SIZE) {
      this.toastr.info($localize`Der Export enthält die ersten ${MAX_PAGE_SIZE}:max: Einträge.`);
    }
    this.state.onPageChange(event);
    this.load();
  }

  public onSortChange(sort: SortDescriptor[]): void {
    this.state.onSortChange(sort);
    this.load();
  }
}
